// PATH: src/components/trials/trialLogic.ts
/**
 * Essais d'extraction (lot L3) : logique pure de l'écran « Essais ».
 *
 * La règle d'héritage est celle du serveur : champ par champ, l'essai
 * l'emporte sur la matière, la matière sur le global. Le formulaire est
 * prérempli avec les valeurs EFFECTIVES de la matière (`estimate` avec
 * `override: {}`) ; seuls les champs modifiés partent dans la surcharge, pour
 * que l'origine affichée (« essai ») dise exactement ce que l'essai change.
 */
import type { FactReview, KnowledgeUnit } from "../../api/knowledge/knowledge";
import type { PillTone } from "../ui/StatusPill";
import type {
  CurriculumProgram,
  EstimateBasis,
  KnowledgeRunResult,
  SettingOrigin,
  StaleReason,
  TrialDisabledFact,
  TrialDocument,
  TrialObjective,
  TrialOverride,
  TrialResolved,
  TrialRole,
  TrialRoleEstimate,
  TrialSide,
  TrialStatus,
  UUID,
} from "../../models/domain";

/** Plafond du serveur : au-delà, 422 `field: "document_ids"`. */
export const MAX_TRIAL_DOCUMENTS = 10;

/** Suivi d'un essai en cours (liste et détail) : un document prend de quelques secondes à une minute. */
export const TRIAL_POLL_MS = 5_000;

/** Même plafond que les réglages Tilmid+ (`maxInstructionsRunes`). */
export const MAX_INSTRUCTIONS = 2000;

export interface TrialForm {
  extraction: { model: string; instructions: string };
  review: { model: string; instructions: string; enabled: boolean };
}

export const EMPTY_TRIAL_FORM: TrialForm = {
  extraction: { model: "", instructions: "" },
  review: { model: "", instructions: "", enabled: true },
};

/**
 * Le formulaire prérempli avec les valeurs effectives (absent = relecture
 * active, comme le serveur). La consigne de l'essai part VIDE : la consigne
 * héritée s'affiche à part, au-dessus, et le champ ne porte que ce qui la
 * remplacerait — la recopier dans le champ laissait croire qu'elle s'ajoutait.
 */
export const formFromResolved = (resolved: TrialResolved): TrialForm => ({
  extraction: { model: resolved.fact_extraction?.model ?? "", instructions: "" },
  review: {
    model: resolved.fact_review?.model ?? "",
    instructions: "",
    enabled: resolved.fact_review?.enabled !== false,
  },
});

/**
 * La surcharge de l'essai : la différence entre le formulaire et les valeurs
 * effectives de la matière (`base`).
 *
 * - Un modèle vidé ne part pas : l'essai garde celui de la matière.
 * - Une consigne vide ne part pas non plus : pour le serveur, une consigne
 *   vide vaut « hérite », un essai ne peut donc pas RETIRER une consigne de
 *   la matière (l'écran le dit sous le champ). Remplie, elle REMPLACE la
 *   consigne héritée (le serveur ne les additionne pas).
 * - Relecture coupée : seul `enabled: false` part, le modèle et la consigne
 *   du relecteur n'ont alors plus d'objet.
 */
export const buildOverride = (form: TrialForm, base: TrialResolved): TrialOverride => {
  const out: TrialOverride = {};

  const extraction: TrialOverride["fact_extraction"] = {};
  const exModel = form.extraction.model.trim();
  if (exModel && exModel !== (base.fact_extraction?.model ?? "")) extraction.model = exModel;
  const exText = form.extraction.instructions.trim();
  if (exText && exText !== (base.fact_extraction?.instructions ?? "").trim()) extraction.instructions = exText;
  if (Object.keys(extraction).length) out.fact_extraction = extraction;

  const baseEnabled = base.fact_review?.enabled !== false;
  const review: TrialOverride["fact_review"] = {};
  if (!form.review.enabled) {
    if (baseEnabled) review.enabled = false;
  } else {
    if (!baseEnabled) review.enabled = true;
    const rvModel = form.review.model.trim();
    if (rvModel && rvModel !== (base.fact_review?.model ?? "")) review.model = rvModel;
    const rvText = form.review.instructions.trim();
    if (rvText && rvText !== (base.fact_review?.instructions ?? "").trim()) review.instructions = rvText;
  }
  if (Object.keys(review).length) out.fact_review = review;

  return out;
};

export const overrideIsEmpty = (o: TrialOverride) => Object.keys(o).length === 0;

export type TrialField = "model" | "instructions" | "enabled";

/**
 * L'origine d'un champ pour l'essai : « essai » s'il part dans la surcharge,
 * sinon celle que le serveur donne pour la matière. Même règle que le
 * résolveur, sans attendre l'aller-retour de l'estimation.
 */
export const fieldOrigin = (
  override: TrialOverride,
  base: TrialResolved,
  role: TrialRole,
  field: TrialField,
): SettingOrigin | undefined => {
  if (override[role]?.[field] !== undefined) return "trial";
  return base[role]?.origin?.[field];
};

export const ORIGIN_LABELS: Record<SettingOrigin, string> = {
  global: "global",
  subject: "matière",
  trial: "essai",
};

export const originLabel = (origin: SettingOrigin | undefined) => (origin ? ORIGIN_LABELS[origin] : "global");

export const ROLE_LABELS: Record<TrialRole, string> = {
  fact_extraction: "Extraction des faits",
  fact_review: "Relecture des faits",
};

/** Ce que dit la base de mesure d'un coût estimé. */
export const basisLabel = (r: Pick<TrialRoleEstimate, "basis" | "model" | "samples">): string => {
  const basis: EstimateBasis = r.basis;
  if (basis === "model") return `mesuré sur ${r.model} (${formatCount(r.samples)} appels en 14 j)`;
  if (basis === "operation")
    return `moyenne tous modèles (${formatCount(r.samples)} appels en 14 j) : ${r.model} a moins de 50 appels`;
  return "aucune mesure : coût inconnu";
};

export const STALE_REASON_LABELS: Record<StaleReason, string> = {
  objective_gone: "l'objectif n'existe plus (chapitre réimporté depuis l'essai)",
  version_changed: "une nouvelle version du chapitre a été importée depuis l'essai",
  passages_changed: "les passages du cours de cet objectif ont changé depuis l'essai",
  extraction_in_progress: "une extraction tourne sur ce chapitre : relancez « Appliquer » une fois finie",
};

/** Une raison inconnue (serveur plus récent) reste lisible, jamais muette. */
export const staleReasonLabel = (reason: string) =>
  STALE_REASON_LABELS[reason as StaleReason] ?? `non appliquée (${reason})`;

export interface StatusMeta {
  label: string;
  tone: PillTone;
}

export const TRIAL_STATUS: Record<TrialStatus, StatusMeta> = {
  running: { label: "En cours", tone: "info" },
  done: { label: "Terminé, à décider", tone: "primary" },
  failed: { label: "Échec", tone: "error" },
  applied: { label: "Appliqué", tone: "success" },
  discarded: { label: "Jeté", tone: "neutral" },
};

export const trialStatus = (status: string): StatusMeta =>
  TRIAL_STATUS[status as TrialStatus] ?? { label: status, tone: "neutral" };

/** « Appliquer » n'a de sens que sur un essai terminé (le serveur répond 409 sinon). */
export const canApply = (status: TrialStatus) => status === "done";

/** Un essai appliqué ne se jette pas ; un essai jeté l'est déjà. */
export const canDiscard = (status: TrialStatus) => status === "running" || status === "done" || status === "failed";

export const anyRunning = (trials: { status: TrialStatus }[]) => trials.some((t) => t.status === "running");

export const selectionError = (count: number): string | null => {
  if (count === 0) return "Choisissez au moins un document.";
  if (count > MAX_TRIAL_DOCUMENTS) return `${MAX_TRIAL_DOCUMENTS} documents au plus par essai.`;
  return null;
};

const usdFormat = (digits: number) =>
  new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: digits });

/**
 * Montant en dollars. Un essai coûte souvent quelques centimes : sous 0,10 $
 * on garde 4 décimales, sinon « 0,00 $ » cacherait un coût réel.
 */
export const formatUsd = (v: number | null | undefined): string => {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  return `${usdFormat(Math.abs(v) < 0.1 ? 4 : 2).format(v)} $`;
};

export const formatCount = (n: number | null | undefined) =>
  n === null || n === undefined ? "—" : new Intl.NumberFormat("fr-FR").format(Math.round(n));

export const RUN_COUNTERS: { key: keyof KnowledgeRunResult; label: string }[] = [
  { key: "objectives", label: "Objectifs" },
  { key: "units", label: "Unités extraites" },
  { key: "units_failed", label: "Objectifs en échec" },
  { key: "disabled_by_review", label: "Faits écartés par la relecture" },
  { key: "demoted_by_review", label: "Relations affaiblies par la relecture" },
  { key: "review_failed", label: "Relectures en échec" },
  { key: "review_partial", label: "Relectures partielles" },
  { key: "review_disabled", label: "Unités non relues (relecture coupée)" },
];

/** Totaux des compteurs sur les documents qui en ont (un document en échec n'en a pas). */
export const sumRunResults = (documents: Pick<TrialDocument, "result">[]): KnowledgeRunResult | null => {
  const results = documents.map((d) => d.result).filter((r): r is KnowledgeRunResult => Boolean(r));
  if (!results.length) return null;
  const zero: KnowledgeRunResult = {
    objectives: 0,
    units: 0,
    disabled_by_review: 0,
    demoted_by_review: 0,
    review_failed: 0,
    review_disabled: 0,
    review_partial: 0,
    units_failed: 0,
    failed_passages: 0,
    passages: 0,
  };
  return results.reduce((acc, r) => {
    const next = { ...acc };
    (Object.keys(zero) as (keyof KnowledgeRunResult)[]).forEach((k) => {
      next[k] = acc[k] + (r[k] ?? 0);
    });
    return next;
  }, zero);
};

/**
 * Les objectifs groupés par document, dans l'ordre des documents de l'essai,
 * puis par ordinal (l'ordre du cours). Un objectif d'un document absent de la
 * liste reste affiché, dans un groupe à part en fin.
 */
export const groupObjectives = (
  documents: Pick<TrialDocument, "document_id" | "title">[],
  objectives: TrialObjective[],
): { document_id: UUID; title: string; objectives: TrialObjective[] }[] => {
  const byDoc = new Map<UUID, TrialObjective[]>();
  objectives.forEach((o) => byDoc.set(o.document_id, [...(byDoc.get(o.document_id) ?? []), o]));
  const sorted = (list: TrialObjective[]) => [...list].sort((a, b) => a.ordinal - b.ordinal);
  const groups = documents.map((d) => ({
    document_id: d.document_id,
    title: d.title,
    objectives: sorted(byDoc.get(d.document_id) ?? []),
  }));
  const known = new Set(documents.map((d) => d.document_id));
  const orphans = objectives.filter((o) => !known.has(o.document_id));
  if (orphans.length) groups.push({ document_id: "", title: "Autres objectifs", objectives: sorted(orphans) });
  return groups;
};

export type FactKind = "term" | "relation" | "step" | "date" | "misconception" | "category";

export const FACT_KIND_LABELS: Record<FactKind, string> = {
  term: "Terme",
  relation: "Relation",
  step: "Étape",
  date: "Date",
  misconception: "Idée fausse",
  category: "Catégorie",
};

/** Un fait mis à plat pour l'affichage : le contenu tel qu'extrait, rien de reformulé. */
export interface FactLine {
  id: string;
  kind: FactKind;
  text: string;
  detail?: string;
}

/**
 * Tous les faits d'une unité, dans l'ordre de l'écran des chapitres (termes,
 * relations, étapes, dates, idées fausses, catégories). Les relations et les
 * catégories citent des termes par identifiant : on les remplace par le terme.
 */
export const factLines = (unit: KnowledgeUnit | null | undefined): FactLine[] => {
  if (!unit) return [];
  const termById = new Map((unit.terms ?? []).map((t) => [t.id, t.term]));
  const term = (id: string | undefined) => (id ? termById.get(id) ?? id : "—");
  return [
    ...(unit.terms ?? []).map((t) => ({ id: t.id, kind: "term" as const, text: t.term, detail: t.definition })),
    ...(unit.relations ?? []).map((r) => ({
      id: r.id,
      kind: "relation" as const,
      text: `${term(r.from)} → ${r.label} → ${term(r.to)}`,
    })),
    ...[...(unit.steps ?? [])]
      .sort((a, b) => a.order - b.order)
      .map((s) => ({ id: s.id, kind: "step" as const, text: `${s.order}. ${s.text}` })),
    ...(unit.dates ?? []).map((d) => ({ id: d.id, kind: "date" as const, text: d.when, detail: d.event })),
    ...(unit.misconceptions ?? []).map((m) => ({
      id: m.id,
      kind: "misconception" as const,
      text: m.claim,
      detail: `Correction : ${m.correction}`,
    })),
    ...(unit.categories ?? []).map((c) => ({
      id: c.id,
      kind: "category" as const,
      text: c.name,
      detail: (c.members ?? []).map(term).join(", ") || "aucun membre",
    })),
  ];
};

export const disabledByLabel = (by: string | undefined): string => {
  if (!by) return "désactivé";
  if (by === "review") return "écarté par la relecture";
  if (by === "report") return "désactivé par signalement";
  if (by.startsWith("editor")) return "désactivé par un éditeur";
  return `désactivé (${by})`;
};

export const disabledMap = (side: TrialSide | null | undefined) =>
  new Map<string, TrialDisabledFact>((side?.disabled ?? []).map((d) => [d.fact_id, d]));

/** Faits d'un côté : total et écartés. */
export const sideCounts = (side: TrialSide | null | undefined) => {
  const lines = factLines(side?.unit);
  const off = disabledMap(side);
  return { total: lines.length, disabled: lines.filter((l) => off.has(l.id)).length };
};

/**
 * Les faits ACTIFS des deux côtés sont-ils les mêmes ? Comparaison du texte
 * extrait mis à plat (sans les identifiants, renumérotés à chaque extraction) :
 * « identique » dit qu'appliquer ne changerait rien aux faits servis.
 */
export const sameActiveFacts = (a: TrialSide | null, b: TrialSide | null): boolean => {
  const key = (side: TrialSide | null) => {
    const off = disabledMap(side);
    return factLines(side?.unit)
      .filter((l) => !off.has(l.id))
      .map((l) => `${l.kind}\u0000${l.text}\u0000${l.detail ?? ""}`)
      .sort()
      .join("\u0001");
  };
  return key(a) === key(b);
};

export type ObjectiveState = "failed" | "empty" | "new" | "same" | "changed";

export const objectiveState = (o: TrialObjective): ObjectiveState => {
  if (o.failed) return "failed";
  if (!o.trial) return "empty";
  if (!o.served) return "new";
  return sameActiveFacts(o.served, o.trial) ? "same" : "changed";
};

export const OBJECTIVE_STATE: Record<ObjectiveState, StatusMeta> = {
  failed: { label: "Extraction en échec", tone: "error" },
  empty: { label: "Rien extrait", tone: "warning" },
  new: { label: "Aucun fait servi aujourd'hui", tone: "info" },
  same: { label: "Faits identiques", tone: "neutral" },
  changed: { label: "Faits différents", tone: "primary" },
};

/** Les verdicts qui disent « non » : ce qu'il faut regarder en premier. */
export const reviewObjections = (review: FactReview | undefined): StatusMeta[] => {
  if (!review) return [];
  const out: StatusMeta[] = [];
  if (!review.supported) out.push({ label: "non confirmé par le passage", tone: "error" });
  // Un type douteux affaiblit la relation sans l'écarter : moins grave.
  if (!review.type_ok) out.push({ label: "type douteux", tone: "warning" });
  if (review.language_ok === false) out.push({ label: "pas dans la langue du passage", tone: "error" });
  return out;
};

export interface SubjectOption {
  id: UUID;
  name: string;
  program_id: UUID;
  program: string;
}

/** Les matières de tous les programmes, groupables par programme (un essai porte sur UNE matière). */
export const subjectOptions = (programs: CurriculumProgram[]): SubjectOption[] =>
  programs.flatMap((p) =>
    [...p.subjects]
      .sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name, "fr"))
      .map((s) => ({ id: s.id, name: s.name, program_id: p.program.id, program: p.program.name || p.program.code })),
  );

/** Nom de chaque niveau, pour situer un document dans la liste. */
export const gradeNames = (programs: CurriculumProgram[]): Map<UUID, string> =>
  new Map(programs.flatMap((p) => p.stages.flatMap((s) => s.grades.map((g) => [g.grade.id, g.grade.name] as [UUID, string]))));

/** Les réglages d'un essai en une ligne : « gpt-4.1 · relu par gpt-4o », « gpt-4.1 · sans relecture ». */
export const settingsSummary = (resolved: TrialResolved | null | undefined): string => {
  const extract = resolved?.fact_extraction?.model ?? "—";
  const review = resolved?.fact_review;
  if (review?.enabled === false) return `${extract} · sans relecture`;
  return `${extract} · relu par ${review?.model ?? "—"}`;
};
